
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../services/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserProfileModal } from '../modals/UserProfileModal';
import AlertBadge from '../AlertBadge';
import AlertsPanel from '../AlertsPanel';
import { Footer } from './Footer';
import { NavDropdown } from './NavDropdown';

interface LayoutProps {
  children: React.ReactNode;
}

type NavigationTab = 'overview' | 'dashboard' | 'ranking' | 'portfolio-analysis' | 'market-data' | 'scenarios' | 'tools' | 'investment-planner' | 'shortlist' | 'retirement' | 'stocks';

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState<'general' | 'security' | 'notifications' | 'activity'>('general');
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);

  // Check for settings routes
  useEffect(() => {
    if (location.pathname === '/settings/notifications') {
      setProfileInitialTab('notifications');
      setShowProfileModal(true);
    }
  }, [location.pathname]);

  // Determine active tab from URL pathname
  const getActiveTab = (pathname: string): NavigationTab => {
    if (pathname === '/leaderboard' || pathname === '/ranking') return 'ranking';
    if (pathname === '/portfolio-analysis') return 'portfolio-analysis';
    if (pathname === '/market-data') return 'market-data';
    if (pathname === '/scenarios') return 'scenarios';
    if (pathname === '/retirement') return 'retirement';

    if (pathname === '/tools' || pathname === '/investment-tools') return 'tools';
    if (pathname === '/investment-planner') return 'investment-planner';
    if (pathname === '/inspection-tracker') return 'shortlist';
    if (pathname === '/stocks') return 'stocks';
    if (pathname === '/properties') return 'dashboard';
    return 'overview';
  };

  const activeTab = getActiveTab(location.pathname);

  // Check if we're on the admin page
  const isAdminPage = location.pathname === '/admin';

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAdminClick = () => {
    navigate('/admin');
  };

  const userName = user?.displayName || user?.email?.split('@')[0] || 'User';
  const userPhotoUrl = user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&size=128&background=4F46E5&color=fff`;

  return (
    <div className="min-h-screen min-h-[100dvh] bg-slate-50">
      {/* Navigation Header */}
      {user && (
        <nav className="bg-white shadow-sm border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              {/* Left side - Logo/Brand */}
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <img
                    className="h-10 w-auto"
                    src="/logo.jpg"
                    alt="CanvasEstate logo"
                  />
                  <div className="ml-2 flex flex-col">
                    <span className="text-xl font-bold text-brand-secondary leading-none">CanvasEstate</span>
                    <span className="text-[0.6rem] text-brand-primary font-bold uppercase tracking-wider">Let's Canvas Your Estate</span>
                  </div>
                </div>
              </div>

              {/* Right side - User menu */}
              <div className="flex items-center space-x-4">
                {/* Portfolio Alerts */}
                <AlertBadge onClick={() => setShowAlertsPanel(true)} aria-label="View portfolio alerts" />

                {isAdmin && !isAdminPage && (
                  <button
                    onClick={handleAdminClick}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-brand-primary bg-emerald-50 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary"
                    aria-label="Open admin panel"
                  >
                    Admin Dashboard
                  </button>
                )}

                {/* User dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-brand-primary rounded-lg p-1"
                    aria-label={`User menu for ${userName}`}
                    aria-expanded={showUserMenu ? "true" : "false"}
                    aria-haspopup="true"
                  >
                    <img
                      src={userPhotoUrl}
                      alt={userName}
                      className="h-8 w-8 rounded-full"
                    />
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-brand-secondary">{userName}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown menu */}
                  {showUserMenu && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowUserMenu(false)}
                      />

                      {/* Menu */}
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                        <div className="py-1">
                          <div className="px-4 py-2 border-b border-slate-100">
                            <p className="text-sm font-medium text-brand-secondary">{userName}</p>
                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                          </div>

                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              setProfileInitialTab('general');
                              setShowProfileModal(true);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                          >
                            <svg className="mr-3 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Profile
                          </button>

                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              navigate('/settings');
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                          >
                            <svg className="mr-3 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Subscription Settings
                          </button>

                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                          >
                            <svg className="mr-3 h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Sign out
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Navigation Tabs - Only show on main app, not admin page */}
      {user && !isAdminPage && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="-mb-px flex space-x-4 h-12 items-center" aria-label="Tabs">
              <NavDropdown
                label="My Portfolio"
                isActive={['dashboard', 'portfolio-analysis', 'ranking', 'stocks'].includes(activeTab)}
                items={[
                  {
                    label: 'Dashboard',
                    onClick: () => navigate('/dashboard'),
                    isActive: activeTab === 'dashboard',
                    title: 'View your portfolio summary'
                  },
                  {
                    label: 'Portfolio Analysis',
                    onClick: () => navigate('/portfolio-analysis'),
                    isActive: activeTab === 'portfolio-analysis',
                    title: 'Deep dive into your portfolio performance'
                  },
                  {
                    label: 'Ranking',
                    onClick: () => navigate('/ranking'),
                    isActive: activeTab === 'ranking',
                    title: 'Compare your portfolio with other investors'
                  },
                  {
                    label: 'Stock Portfolio',
                    onClick: () => navigate('/stocks'),
                    isActive: activeTab === 'stocks',
                    title: 'Manage your stock portfolio and investments'
                  },
                ]}
              />

              <NavDropdown
                label="Strategy & Tools"
                isActive={['investment-planner', 'scenarios', 'tools', 'retirement'].includes(activeTab)}
                items={[
                  {
                    label: 'Investment Planner',
                    onClick: () => navigate('/investment-planner'),
                    isActive: activeTab === 'investment-planner',
                    title: 'Plan your property investment strategy'
                  },
                  {
                    label: 'Scenario Planner',
                    onClick: () => navigate('/scenarios'),
                    isActive: activeTab === 'scenarios',
                    title: 'Model different investment scenarios and outcomes'
                  },
                  {
                    label: 'Investment Tools',
                    onClick: () => navigate('/tools'),
                    isActive: activeTab === 'tools',
                    title: 'Calculators for yield, stamp duty, and more'
                  },
                  {
                    label: 'Property Calculators',
                    onClick: () => navigate('/calculators'),
                    isActive: location.pathname.startsWith('/calculators'),
                    title: 'Seven free and unlimited property calculators'
                  },
                  {
                    label: 'Financial Freedom',
                    onClick: () => navigate('/retirement'),
                    isActive: activeTab === 'retirement',
                    title: 'Project your journey to independence with AI-powered scenario analysis'
                  },
                ]}
              />

              <NavDropdown
                label="Property Research"
                isActive={['market-data', 'shortlist'].includes(activeTab)}
                items={[
                  {
                    label: 'Market Data',
                    onClick: () => navigate('/market-data'),
                    isActive: activeTab === 'market-data',
                    title: 'Explore suburb statistics and market trends'
                  },
                  {
                    label: 'Pre-Purchase Hub',
                    onClick: () => navigate('/inspection-tracker'),
                    isActive: activeTab === 'shortlist',
                    title: 'Track properties you\'re considering before purchase'
                  },
                ]}
              />


            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          if (location.pathname === '/settings/notifications') {
            navigate('/');
          }
        }}
        initialTab={profileInitialTab}
      />

      {/* Portfolio Alerts Panel */}
      <AlertsPanel
        isOpen={showAlertsPanel}
        onClose={() => setShowAlertsPanel(false)}
      />
    </div>
  );
};
